// Test accounts.
//
// Steve's test accounts are indistinguishable from real ones in Firestore:
// no flag, nothing in the roles array, only a first name he happens to
// recognise. That is fine for him and useless to code, so anything they buy is
// counted as club revenue — ClubReports sums every paid `purchases` row, and a
// dozen $0 and $1 test transactions sit in the revenue tile and the transaction
// count as if they were sales.
//
// The marker is an explicit `isTest: true` on the account, set by hand. It is
// deliberately NOT a name match: report totals should not depend on a naming
// habit, and a real member called Bigtime should not vanish from the books.
//
// Note this is a REPORTING exclusion, not a delete. A test account can hold
// genuine Stripe charges — testing live-mode checkout leaves real money behind
// — so the rows stay where they are and the bank can still be reconciled
// against them. Unset the flag and they come straight back.

/** True if this user or athlete doc is marked as a test account. */
export function isTestAccount(person) {
  return person?.isTest === true;
}

/**
 * Every id a test account is known by — its own doc id, and the userId an
 * athlete record points at. Purchases carry either, depending on which flow
 * wrote them, so both have to be in the set for the filter to catch them all.
 */
export function testAccountIds(users = [], athletes = []) {
  const ids = new Set();
  for (const u of users) if (isTestAccount(u) && u.id) ids.add(u.id);
  for (const a of athletes) {
    if (!isTestAccount(a)) continue;
    if (a.id) ids.add(a.id);
    if (a.athleteId) ids.add(a.athleteId);
    if (a.userId) ids.add(a.userId);
  }
  return ids;
}

/**
 * Purchases with anything belonging to a test account dropped.
 *
 * Two shapes, because there are two. Most test rows point at an account that
 * still exists and are matched by id. Some point at an account that was deleted
 * out from under them — the row is all that is left, so there is nothing to
 * flag but the row itself. `isTest` on the purchase covers that case.
 *
 * Deleting those orphans is the wrong fix: they carry cs_live_ session ids, so
 * the money really moved and `purchases` would stop agreeing with the bank.
 */
export function excludeTestPurchases(purchases = [], testIds = new Set()) {
  return purchases.filter(p =>
    !isTestAccount(p) && !testIds.has(p.userId) && !testIds.has(p.athleteId));
}
